import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
  Download,
  FileCode2,
  FilePlus2,
  Loader2,
  Package,
  PanelRightClose,
  PanelRightOpen,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/bols/status-badge";
import { AddDocumentsDialog } from "@/components/review/add-documents-dialog";
import { useReview } from "@/components/review/context";
import { ReviewProvider } from "@/components/review/provider";
import { DocumentPane } from "@/components/review/document-pane";
import { InvoicesPanel } from "@/components/review/invoices-panel";
import { ProgressCard } from "@/components/review/progress-card";
import { SadForm } from "@/components/review/sad-form";
import { SplitDialog } from "@/components/review/split-dialog";
import { SplitStateCard } from "@/components/review/split-state-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError } from "@/lib/api";
import { saveFile } from "@/lib/download";
import { bolQuery, bolsApi, splitUnresolved, STATUS_LABEL, type BolStatus } from "@/lib/bols";

export const Route = createFileRoute("/_authenticated/bols/$bolId")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(bolQuery(params.bolId)),
  component: BolReviewPage,
});

const NEXT: Record<BolStatus, { to: BolStatus; label: string; variant?: "outline" }[]> = {
  draft: [{ to: "review", label: "Start review" }],
  extracting: [],
  review: [
    { to: "ready", label: "Mark ready", variant: "outline" },
    { to: "draft", label: "Back to draft", variant: "outline" },
  ],
  ready: [{ to: "review", label: "Reopen review", variant: "outline" }],
  exported: [{ to: "review", label: "Reopen review", variant: "outline" }],
};

function BolReviewPage() {
  const { bolId } = Route.useParams();
  const { data: bol } = useSuspenseQuery(bolQuery(bolId));
  return (
    <ReviewProvider bol={bol}>
      <ReviewLayout />
    </ReviewProvider>
  );
}

function ReviewLayout() {
  const { bol, refresh } = useReview();
  const queryClient = useQueryClient();
  // A proposal waiting on arrival opens the split screen right away.
  const [splitting, setSplitting] = useState(bol.split_status === "suggested");
  const [addingDocuments, setAddingDocuments] = useState(false);
  const [showDocument, setShowDocument] = useState(true);
  const [xmlPreview, setXmlPreview] = useState<string | null>(null);

  const extract = useMutation({
    mutationFn: () => bolsApi.extract(bol.id),
    onSuccess: (updated) => {
      queryClient.setQueryData(bolQuery(bol.id).queryKey, updated);
      toast.info("Reading documents… this page refreshes itself.");
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Could not start extraction"),
  });

  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useMutation({
    mutationFn: () => bolsApi.deleteBol(bol.id),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: bolQuery(bol.id).queryKey });
      await queryClient.invalidateQueries({ queryKey: ["bols"], exact: false });
      toast.success(`Bill of lading ${bol.reference || ""} deleted`.trim());
      void navigate({ to: "/bols" });
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Could not delete"),
  });

  const resort = useMutation({
    mutationFn: () => bolsApi.suggestSplit(bol.id),
    onSuccess: (updated) => {
      queryClient.setQueryData(bolQuery(bol.id).queryKey, updated);
      toast.info("Sorting pages… this page refreshes itself.");
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Could not start sorting"),
  });

  const setStatus = useMutation({
    mutationFn: (status: BolStatus) => bolsApi.setStatus(bol.id, status),
    onSuccess: (updated) => {
      queryClient.setQueryData(bolQuery(bol.id).queryKey, updated);
      void queryClient.invalidateQueries({ queryKey: ["bols"], exact: false });
      toast.success(`Status: ${STATUS_LABEL[updated.status]}`);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Could not change status"),
  });

  // One step: store the XML (marks the bill exported) and hand over the package the
  // broker attaches in ASYCUDA. The XML is inside the package too.
  const exportBol = useMutation({
    mutationFn: async () => {
      const updated = await bolsApi.export(bol.id);
      queryClient.setQueryData(bolQuery(bol.id).queryKey, updated);
      void queryClient.invalidateQueries({ queryKey: ["bols"], exact: false });
      return bolsApi.downloadPackage(bol.id);
    },
    onSuccess: (file) => {
      saveFile(file);
      toast.success("Exported; the ASYCUDA package is downloading");
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Export failed"),
  });

  const downloadPackage = useMutation({
    mutationFn: () => bolsApi.downloadPackage(bol.id),
    onSuccess: saveFile,
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Could not build the package"),
  });

  const preview = useMutation({
    mutationFn: () => bolsApi.previewXml(bol.id),
    onSuccess: (xml) => setXmlPreview(xml),
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Could not build the XML"),
  });

  const hasExtractedSomething =
    !!bol.extracted || bol.invoices.some((i) => i.extraction_status === "done");
  const failedInvoices = bol.invoices.filter((i) => i.extraction_status === "failed").length;
  const readLabel = bol.extraction_is_stale
    ? "Restart reading"
    : failedInvoices
      ? `Retry ${failedInvoices} failed invoice${failedInvoices === 1 ? "" : "s"}`
      : hasExtractedSomething
        ? "Re-read documents"
        : "Read documents";
  const unresolved = splitUnresolved(bol.split_status);
  const hasAnyDocument =
    !!bol.document_url || !!bol.bundle_document_url || bol.invoices.some((i) => i.document_url);

  return (
    <div className="flex h-[calc(100vh-3rem)] flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Link to="/bols" className="text-muted-foreground hover:underline">
            ← Bills of lading
          </Link>
          <span className="text-muted-foreground">|</span>
          <h1 className="text-base font-semibold">
            Single Administrative Document
            <span className="text-muted-foreground font-normal">
              {" · "}
              {bol.manifest_reference ? `${bol.manifest_reference}/` : ""}
              {bol.reference || "no B/L number yet"}
            </span>
          </h1>
          <StatusBadge status={bol.status} />
          {bol.status !== "extracting" && bol.issues.length > 0 && (
            <span className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
              Needs review · {bol.issues.length} blocking
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {bol.is_editable && (
            <Button variant="outline" size="sm" onClick={() => setAddingDocuments(true)}>
              <FilePlus2 className="size-4" /> Add documents
            </Button>
          )}
          {bol.can_split && !unresolved && (
            <Button variant="outline" size="sm" onClick={() => setSplitting(true)}>
              Re-split pages
            </Button>
          )}
          {bol.can_extract && (
            <Button
              variant={bol.extraction_is_stale || failedInvoices ? "default" : "secondary"}
              size="sm"
              disabled={extract.isPending}
              onClick={() => extract.mutate()}
              title={
                bol.extraction_is_stale
                  ? "This reading has been running unusually long; the worker may have stopped. Restart it."
                  : failedInvoices
                    ? "Failed invoices are read again; finished ones are kept"
                    : undefined
              }
            >
              {readLabel}
            </Button>
          )}
          {bol.status === "extracting" && !bol.extraction_is_stale && (
            <Button variant="secondary" size="sm" disabled>
              Reading documents…
            </Button>
          )}
          {!unresolved &&
            NEXT[bol.status].map((action) => (
              <Button
                key={action.to}
                size="sm"
                variant={action.variant}
                disabled={setStatus.isPending || (action.to === "ready" && bol.issues.length > 0)}
                title={
                  action.to === "ready" && bol.issues.length > 0
                    ? "Resolve the open issues first"
                    : undefined
                }
                onClick={() => setStatus.mutate(action.to)}
              >
                {action.label}
              </Button>
            ))}
          <Button
            variant="outline"
            size="sm"
            disabled={preview.isPending}
            onClick={() => preview.mutate()}
            title="Show the XML as it would be exported now"
          >
            <FileCode2 className="size-4" /> XML preview
          </Button>
          {bol.status === "exported" && bol.exported_document_url ? (
            <>
              <Button size="sm" variant="outline" asChild>
                <a href={bolsApi.downloadUrl(bol.id)} target="_blank" rel="noopener">
                  <Download className="size-4" /> Download XML
                </a>
              </Button>
              <Button
                size="sm"
                disabled={downloadPackage.isPending}
                onClick={() => downloadPackage.mutate()}
                title="XML + bill of lading + invoices merged into one PDF, ready to attach in ASYCUDA"
              >
                {downloadPackage.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" /> Preparing package…
                  </>
                ) : (
                  <>
                    <Package className="size-4" /> Download ASYCUDA package
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              disabled={!bol.can_export || exportBol.isPending}
              title={
                bol.can_export
                  ? "Store the XML, mark the bill exported and download the ASYCUDA package"
                  : "Resolve the open issues first"
              }
              onClick={() => exportBol.mutate()}
            >
              {exportBol.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Exporting…
                </>
              ) : (
                <>
                  <Package className="size-4" /> Export
                </>
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setConfirmDelete(true)}
            title="Delete this bill with its files, invoices and items"
          >
            <Trash2 className="size-4" /> Delete
          </Button>
          <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete bill of lading {bol.reference || ""}?</AlertDialogTitle>
                <AlertDialogDescription>
                  The transport document, {bol.invoices.length} invoice
                  {bol.invoices.length === 1 ? "" : "s"} with their lines, {bol.posts.length} item
                  {bol.posts.length === 1 ? "" : "s"}
                  {bol.status === "exported" ? ", and the exported XML" : ""} are removed for good.
                  This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive hover:bg-destructive/90 text-white"
                  disabled={remove.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    remove.mutate();
                  }}
                >
                  {remove.isPending ? "Deleting…" : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDocument((v) => !v)}
            title={showDocument ? "Hide documents" : "Show documents"}
          >
            {showDocument ? (
              <PanelRightClose className="size-4" />
            ) : (
              <PanelRightOpen className="size-4" />
            )}
          </Button>
        </div>
      </div>

      {bol.extraction_error && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/5 rounded-md border p-3 text-sm"
        >
          <div className="font-medium">
            Extraction problems
            {bol.extraction_finished_at && (
              <span className="text-muted-foreground font-normal">
                {" "}
                · from the reading of {new Date(bol.extraction_finished_at).toLocaleString()};
                re-read the documents to try again
              </span>
            )}
          </div>
          <pre className="mt-1 font-sans whitespace-pre-wrap">{bol.extraction_error}</pre>
        </div>
      )}

      <div
        className={
          // The single row must be minmax(0,1fr), not auto: an auto row grows to the
          // form's content and pushes the page past the viewport on short screens.
          showDocument && hasAnyDocument
            ? "grid min-h-0 flex-1 grid-cols-[minmax(0,7fr)_minmax(0,3fr)] grid-rows-[minmax(0,1fr)] gap-3"
            : "grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)]"
        }
      >
        {/* @container: the form lays itself out by this column's width (PDF open or not).
            relative: the form's dropdowns render visually hidden native <select>s with
            position:absolute; without a positioned ancestor inside this scroll column they
            are placed against the page, escape the clipping and stretch the page by the
            form's full height. */}
        <div className="@container relative min-h-0 overflow-y-auto pr-1">
          <div className="space-y-3">
            <SplitStateCard
              bol={bol}
              onOpenSplit={() => setSplitting(true)}
              onRetry={() => resort.mutate()}
              retrying={resort.isPending}
            />
            {bol.status === "extracting" && <ProgressCard bol={bol} />}
            <Tabs defaultValue="declaration">
              <TabsList>
                <TabsTrigger value="declaration">Declaration</TabsTrigger>
                <TabsTrigger value="invoices">Invoices ({bol.invoices.length})</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>
              <TabsContent value="declaration" className="pt-3">
                <SadForm />
              </TabsContent>
              <TabsContent value="invoices" className="pt-3">
                <InvoicesPanel />
              </TabsContent>
              <TabsContent value="history" className="pt-3">
                <HistoryTab />
              </TabsContent>
            </Tabs>
          </div>
        </div>
        {showDocument && hasAnyDocument && (
          <div className="min-h-0">
            <DocumentPane />
          </div>
        )}
      </div>

      <SplitDialog bol={bol} open={splitting} onOpenChange={setSplitting} onDone={refresh} />
      <AddDocumentsDialog
        bol={bol}
        open={addingDocuments}
        onOpenChange={setAddingDocuments}
        onDone={refresh}
      />

      <Dialog open={xmlPreview !== null} onOpenChange={(open) => !open && setXmlPreview(null)}>
        <DialogContent className="flex max-h-[90vh] w-[95vw] flex-col overflow-hidden sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>ASYCUDA XML preview</DialogTitle>
            <DialogDescription>
              Built from the current data; nothing is exported until you press Export.
            </DialogDescription>
          </DialogHeader>
          <pre className="bg-muted/40 min-h-0 flex-1 overflow-auto rounded border p-3 font-mono text-xs">
            {xmlPreview}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HistoryTab() {
  const { bol } = useReview();
  const extracted = bol.extracted ?? {};
  const entries = Object.entries(extracted).filter(([key, value]) => {
    if (key === "notes") return false;
    if (value === null || value === "" || value === "unknown") return false;
    return !(Array.isArray(value) && value.length === 0);
  });
  return (
    <div className="grid grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
          <CardDescription>
            Created {new Date(bol.created_at).toLocaleString()} by {bol.created_by_email}
            {bol.exported_at &&
              ` · exported ${new Date(bol.exported_at).toLocaleString()} by ${bol.exported_by_email ?? "?"}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1 text-sm">
            {bol.status_events.map((event) => (
              <li key={event.id} className="flex flex-wrap gap-x-3">
                <span className="text-muted-foreground">
                  {new Date(event.created_at).toLocaleString()}
                </span>
                <span>
                  {STATUS_LABEL[event.from_status]} → {STATUS_LABEL[event.to_status]}
                </span>
                <span className="text-muted-foreground">{event.user_email}</span>
                {event.note && <span className="italic">“{event.note}”</span>}
              </li>
            ))}
            {bol.status_events.length === 0 && (
              <li className="text-muted-foreground">No status changes yet.</li>
            )}
          </ul>
        </CardContent>
      </Card>
      {entries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Read from the transport document</CardTitle>
            <CardDescription>
              Raw values as printed. Header fields were filled from these only where they were
              empty.
              {typeof extracted.notes === "string" &&
                extracted.notes &&
                ` Notes: ${extracted.notes}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
              {entries.map(([key, value]) => (
                <div key={key} className="contents">
                  <dt className="text-muted-foreground">{key.replaceAll("_", " ")}</dt>
                  <dd className="truncate">
                    {Array.isArray(value) ? value.join(", ") : String(value)}
                  </dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
